/** @module csi/widget/froalaTextEditor */
define([
	"dojo/_base/array",
	"dojo/_base/config",
	"dojo/_base/declare",
	"dojo/keys",
	"dojo/_base/lang",
	"dijit/form/_FormWidget",
	"csi",
	"designSystem/fonts",
	"csi/html",
	"csi/locale",
	"csi/sessionConstants",
	"csi/state",
	"./Froala"
], function(array, config, declare, keys, lang, _FormWidget, csi, fonts, html, locale, sessionConstants, state, Froala) {

	let _language = locale.ENGLISH;

	function _updateRichTextTags(/*String*/value) {
		// summary: replaces all old font tags, anchors missing href tags with span tags. support for legacy data
		var fontSizes = [
			'10px', // 1
			'13px', // 2
			'16px', // 3
			'18px', // 4
			'24px', // 5
			'32px', // 6
			'48px'  // 7
		];
		var tempNode = $(document.createElement("div"));
		tempNode.append($.parseHTML(value));

		tempNode.find('font, a:not([href])').replaceWith(function() {
			var attrs = this.attributes;
			var span = $('<span />').append($(this).contents());
			var style = "";

			for (var i = 0; i < attrs.length; i++) {
				var name = attrs[i].name;
				var value = attrs[i].value;

				if (name == 'style') {
					style += value;
				} else if (name == 'color') {
					style += name + ": " + value + "; ";
				} else if (name == 'size') {
					name = 'font-size';
					value = fontSizes[value - 1];
					style += name + ": " + value + "; ";
				}
			}

			if (style) {
				span.attr("style", style);
			}
			return span;
		});

		return tempNode.html();
	}

	const _FroalaTextEditor = declare(_FormWidget, {
		connections: [],
		editor: null,
		editableNode: null,
		focusNode: null,
		_lastSetValue: null,
		language: "en",
		refreshValue: false,

		//convert to params
		inlineEditor: false,
		canvasText: false,

		//override template to remove inputcontainerDom
		templateString: '<div class="dijit dijitReset dijitInline dijitLeft" id="widget_${id}" role="presentation" ></div>',

		/**
		 * Extension point indicating that the editor is initialized
		 */
		onInit: function() { },

		/**
		 * overridding default onBlur calls.
		 */
		_onBlur: function() { },

		// summary: create froala editor
		_init: function() {
			let toolbarItems = [
				'bold',
				'italic',
				'underline',
				'strikeThrough',
				'fontFamily',
				'fontSize',
				'textColor',
				'backgroundColor',
				'paragraphFormat',
				'clearFormatting',
				'|',
				'align',
				'formatOL',
				'formatUL',
				'indent',
				'outdent',
				'|',
				'insertLink',
				'insertTable',
				'undo',
				'redo',
			];

			let placeholderText = "";

			// for inline toolbar we want to display toolbar in 3 rows
			if (this.inlineEditor && !this.canvasText) {
				toolbarItems = [
					'bold',
					'italic',
					'underline',
					'strikeThrough',
					'fontFamily',
					'fontSize',
					'textColor',
					'backgroundColor',
					'-',
					'paragraphFormat',
					'clearFormatting',
					'align',
					'formatOL',
					'formatUL',
					'indent',
					'outdent',
					'-',
					'insertLink',
					'insertTable',
					'undo',
					'redo',
				];
				placeholderText = locale.resource('EditorPlaceHolder');
			}

			const linkEditButtons = this.inlineEditor ? ['linkOpen', 'linkEdit', 'linkRemove'] : ['linkOpen', 'linkRemove'];

			const froalaOptions = {
				attribution: false,
				key: sessionConstants.froalaLicense,
				theme: 'csi-froala',
				toolbarInline: this.inlineEditor,
				linkList: [],
				linkEditButtons,
				listAdvancedTypes: false,
				fontFamily: fonts.getFontFamilies(),
				fontFamilySelection: false,
				htmlRemoveTags: ['script', 'style', 'base'],
				language: this.language,
				imagePaste: false,
				paragraphFormat: {
					N: 'Normal',
					H1: 'Heading 1',
					H2: 'Heading 2',
					H3: 'Heading 3',
					H4: 'Heading 4'
				},
				heightMin: this.heightMin || "20px",
				heightMax: this.heightMax || 'auto',
				width: this.width,
				height: this.height,
				pasteDeniedAttrs: ['class', 'id'],
				quickInsertButtons: ['ul', 'ol', 'table'],
				// C8-39382
				// render popups inside the dialog, otherwise the HEX input for table cell background color is not editable
				// more info here: https://github.com/froala/react-froala-wysiwyg/issues/122
				scrollableContainer: this.scrollableContainer || "body",
				toolbarButtons: toolbarItems,
				toolbarButtonsMD: toolbarItems,
				toolbarButtonsSM: toolbarItems,
				placeholderText,
				keepFormatOnDelete: true,
				toolbarContainer: (this.params && this.params.toolbarContainer) ? this.params.toolbarContainer : null,
				paragraphMultipleStyles: true,
				charCounterCount: false,
				wordPasteModal: false,
				htmlAllowedAttrs: ['target', 'title', 'href', 'alt', 'src', 'style', 'span', 'class', 'rowspan', 'colspan'],
				zIndex: 1050, // The zIndex to set on the editor toolbars and popups. Above 949 (that is where dijit popups start)
				events: {
					"blur": lang.hitch(this, this._checkIfContentsChanged),
					'paste.beforeCleanup': function(clipboard_html) {
						// triggered after the content is pasted from the clipboard into the Froala Rich Text Editor.
						// if a string is returned the new string will be used as the pasted content.
						return clipboard_html.replace(/background-color\: rgb\(213\, 227\, 245\)|background-color\: rgb\(234\, 241\, 250\)|background-color\: rgb\(255\, 255\, 255\)/g, "");
					}
				}
			}

			this.froalaEditor = new Froala(this.domNode, froalaOptions, () => {
				// widget base sets value before editor is created, so set the editor's value
				this.froalaEditor.html.set(this._lastSetValue);

				array.forEach(this.connections, this.disconnect);
				this.connections = [];

				if (this.params) {
					this.set('disabled', this.params.disabled);

					if (this.params.inlineEditor && !this.params.canvasText) {
						this.froalaEditor.events.on('click', lang.hitch(this, this._showToolbarForEmptyElement));
						this.froalaEditor.events.on('keyup', lang.hitch(this, this._showToolbarForEmptyElement));
					}
				}

				function handleEvent(e) {
					var charOrCode;

					if (e.type == "keydown") {
						charOrCode = e.keyCode;

						switch (charOrCode) { // ignore state keys and enter key
							case keys.SHIFT:
							case keys.ALT:
							case keys.CTRL:
							case keys.META:
							case keys.CAPS_LOCK:
							case keys.NUM_LOCK:
							case keys.SCROLL_LOCK:
							case keys.ENTER:
								return;
						}
					}

					//handle keyboard events like tab and escape
					// create fake event to set charOrCode and to know if preventDefault() was called
					var faux = {faux: true}, attr;
					for (attr in e) {
						if (!/^(layer[XY]|returnValue|keyLocation)$/.test(attr)) { // prevent WebKit warnings
							var v = e[attr];
							if (typeof v != "function" && typeof v != "undefined") {
								faux[attr] = v;
							}
						}
					}

					lang.mixin(faux, {
						charOrCode: charOrCode,
						_wasConsumed: false,
						preventDefault: function() {
							faux._wasConsumed = true;
							e.preventDefault();
						},
						stopPropagation: function() {
							e.stopPropagation();
						}
					});

					// give web page author a chance to consume the event
					if (this.onInput(faux) === false) { // return false means stop
						faux.preventDefault();
						faux.stopPropagation();
					}
					if (faux._wasConsumed) {
						return;
					} // if preventDefault was called
				}

				this.connections.push(this.connect(this.focusNode, "click, keydown, keypress, input", lang.hitch(this, handleEvent)));

				// the editor's creation is delayed, so notify listeners that editor is now initialized
				this.onInit();
			});
		},

		/*
			Show inline toolbar if element is empty.
			We are not using froala option toolbarVisibleWithoutSelection, since it shows up toolbar event for non empty lines.
		*/
		_showToolbarForEmptyElement: function(e) {
			const editor = this.froalaEditor;
			let selectedElement = editor.selection.element();

			if (!editor.node.isBlock(selectedElement)) {
				selectedElement = editor.node.blockParent(selectedElement);
			}
			// If element if empty then set toolbarVisibleWithoutSelection to true else false.
			if (selectedElement && editor.node.isEmpty(selectedElement) && editor.node.isElement(selectedElement.parentNode)) {
				editor.opts.toolbarVisibleWithoutSelection = true;
			} else {
				editor.opts.toolbarVisibleWithoutSelection = false;
			}
		},

		_checkIfContentsChanged: function(e) {
			const editor = this.froalaEditor;

			// This function could be called multiple times, and if called before editor is instantiated error will occur.
			if (editor.html) {
				// call onBlur
				this.onBlur(e);

				// check if contents are modified since last blur, fire onChange if changed
				var currentValue = editor.html.get();
				// check that last value differs from current value
				if (currentValue !== this._lastSetValue) {
					this.onChange(currentValue);
					this._lastSetValue = currentValue;
				}
			}
		},

		constructor: function() {
			this.language = _language;
		},

		buildRendering: function() {
			this.inherited(arguments);

			if (this.domNode) {
				this.editor = $(this.domNode);
				this.focusNode = this.domNode;
				this._init();
			}
		},

		focus: function() {
			if (this.editor[0].offsetParent) {
				this.editor[0].offsetParent.focus();
			}
		},

		onInput: function(/*Event*/e) {
			// summary: extension point for handling special keys in editor
		},

		onChange: function(/*Object*/value) {
			// summary: extension point for handling on change in editor
		},

		_setDisabledAttr: function(/*Boolean*/disabled) {
			const editor = this.froalaEditor;

			// This function could be called multiple times, and if called before editor is instantiated error will occur.
			if (editor.edit) {
				// summary: control widget enabled/disabled status
				if (disabled) {
					editor.edit.off();
				} else {
					editor.edit.on();
				}
			}
		},

		_setValueAttr: function( /*String|Boolean*/ newValue, /*Boolean*/ priorityChange) {
			const editor = this.froalaEditor;

			// summary: set rich text content
			if (newValue) {
				var value = _updateRichTextTags((this.name && newValue[this.name] != undefined) ? newValue[this.name] : newValue);
				this._lastSetValue = newValue;
				this.inherited(arguments, [newValue]);

				// This function could be called multiple times, and if called before editor is instantiated error will occur.
				if (editor.html) {
					editor.html.set(value);
				}
			}
		},

		_getValueAttr: function() {
			const editor = this.froalaEditor;

			// This function could be called multiple times, and if called before editor is instantiated error will occur.
			if (editor.html) {
				// summary: get rich text content
				var value = editor.html.get(true);

				if (value.indexOf("<br") == 0 && value.length >= 6) {
					value = value.replace("<br />", "");
				}

				// strip string of extra characters and remove any pasted html that might be dangerous
				return html.sanitize(value);
			}
		},

		/**
		 * Apply style to text with CentricToolbar plugin
		 * @param  {String} style
		 * @param  {String} value
		 */
		applyTextStyle: function(style, value) {
			const toolbar = this.froalaEditor.CentricToolbar;

			// This function could be called multiple times, and if called before editor is instantiated error will occur.
			if (toolbar) {
				toolbar.applyTextStyle({style, value});
			}
		},

		/**
		 * Make froala editor uneditable
		 */
		editOff: function() {
			const editor = this.froalaEditor;

			// This function could be called multiple times, and if called before editor is instantiated error will occur.
			if (editor.edit) {
				editor.edit.off();
			}
		},

		/**
		 * Make froala editor editable
		 */
		editOn: function() {
			const editor = this.froalaEditor;

			// This function could be called multiple times, and if called before editor is instantiated error will occur.
			if (editor.edit) {
				editor.edit.on();
				editor.events.focus();
			}
		},

		/**
		 * Set caret postion at end of text
		 */
		setCaretAtEnd: function() {
			const toolbar = this.froalaEditor.CentricToolbar;

			// This function could be called multiple times, and if called before editor is instantiated error will occur.
			if (toolbar) {
				toolbar.setCaretAtEnd();
			}
		},

		/**
		 * Clear selected text formatting
		 */
		clearFormatting: function() {
			const toolbar = this.froalaEditor.CentricToolbar;

			// This function could be called multiple times, and if called before editor is instantiated error will occur.
			if (toolbar) {
				toolbar.clearFormatting();
			}
		},

		/**
		 * Check current selection format
		 * @param  {String} format
		 */
		checkFormat: function(format) {
			const toolbar = this.froalaEditor.CentricToolbar;

			// This function could be called multiple times, and if called before editor is instantiated error will occur.
			if (toolbar) {
				return toolbar.checkFormat(format);
			}
		},

		/**
		 * Clear the current selection on blur event
		 */
		clearSelection: function() {
			const editor = this.froalaEditor;

			// This function could be called multiple times, and if called before editor is instantiated error will occur.
			if (editor.selection) {
				editor.selection.clear();
			}
		}
	});

	//unavailable translations for froala are commented off
	//To do:- these unavailable language files have to be created
	const LANGUAGES = {
		//		"af": "Afrikaans (Afrikaans)",
		//		"bg": "Bulgarian (Български)",
		//		"bn": "Bengali (বাংলা)",
		"cs": "cs",
		"da": "da",
		"de": "de",
		"el": "el",
		"en_GB": "en_gb",
		"es": "es",
		"es_419": "es",
		"et": "et",
		"fi": "fi",
		"fr": "fr",
		//		"ga": "Irish (Gaeilge)",
		"he": "he",
		//		"hi": "Hindi (हिन्दी)",
		"hr": "hr",
		"hu": "hu",
		"it": "it",
		"ja": "ja",
		"ko": "ko",
		//		"lb": "Luxembourgish (Lëtzebuergesch)",
		//		"lt": "Lithuanian (Lietuvių)",
		//		"lv": "Latvian (Latviešu)",
		//		"ms": "Malay (Bahasa Melayu)",
		//		"mt": "Maltese (Malti)",
		"nb": "nb",
		"nl": "nl",
		"pl": "pl",
		"pt": "pt_pt",
		"pt_BR": "pt_br",
		"ro": "ro",
		"ru": "ru",
		"sk": "sk",
		//		"sl": "Slovenian (Slovenščina)",
		"sv": "sv",
		"th": "th",
		"tr": "tr",
		"zh": "zh_cn",
		"zh_TW": "zh_tw"
	};

	/**
	 * Determine language to use for Froala and load resources
	 * @return {Deferred|null}
	 */
	_FroalaTextEditor.loadLanguageResources = () => {
		_language = LANGUAGES[locale.getLocale()] || locale.ENGLISH;

		if (_language != locale.ENGLISH) {
			// this is needed for language files to load - not sure why it is not populated automatically by Froala if it is required by those files???
			window.FroalaEditor = Froala;

			return csi.loadScript(csi.urlForModule("froalaLanguages/" + _language));
		}

		return null;
	};

	return _FroalaTextEditor;
});
